import { useEffect, useState } from "react";
import api, { fmt, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Truck } from "lucide-react";

const PARAM_FIELDS = [
  { key: "valor_adquisicion", label: "Valor de adquisición", dec: 2 },
  { key: "valor_llantas", label: "Valor de llantas", dec: 2 },
  { key: "valor_rescate", label: "Valor de rescate", dec: 2 },
  { key: "vida_economica_horas", label: "Vida económica (h)", dec: 0 },
  { key: "horas_por_anio", label: "Horas por año", dec: 0 },
  { key: "tasa_interes_anual", label: "Tasa de interés (%)", dec: 2 },
  { key: "prima_seguro_anual", label: "Prima seguro (%)", dec: 2 },
  { key: "coef_mantenimiento", label: "Coef. mantenimiento", dec: 2 },
  { key: "potencia_hp", label: "Potencia (HP)", dec: 0 },
  { key: "factor_operacion", label: "Factor de operación", dec: 2 },
  { key: "consumo_combustible_lt_hp_hr", label: "Consumo (lt/HP·h)", dec: 2 },
  { key: "precio_combustible", label: "Precio combustible", dec: 2 },
  { key: "vida_llantas_horas", label: "Vida llantas (h)", dec: 0 },
  { key: "salario_operador_hora", label: "Salario operador/h", dec: 2 },
];

const COST_ROWS = [
  ["depreciacion", "Depreciación"],
  ["inversion", "Inversión"],
  ["seguros", "Seguros"],
  ["mantenimiento", "Mantenimiento"],
  ["combustible", "Combustible"],
  ["llantas", "Llantas"],
  ["operacion", "Operación"],
];

const emptyParams = PARAM_FIELDS.reduce((a, f) => ({ ...a, [f.key]: 0 }), {});

export default function EquiposView() {
  const { user } = useAuth();
  const canWrite = user?.role !== "consulta";
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/equipos");
      setItems(data);
      if (data.length && !selected) setSelected(data[0]);
      else if (selected) setSelected(data.find((d) => d.id === selected.id) || data[0] || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const save = async () => {
    const d = dialog.data;
    try {
      if (dialog.mode === "edit") await api.put(`/equipos/${d.id}`, d);
      else await api.post("/equipos", d);
      toast.success("Equipo guardado");
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (eq) => {
    if (!window.confirm(`¿Eliminar ${eq.codigo}?`)) return;
    await api.delete(`/equipos/${eq.id}`);
    toast.success("Equipo eliminado");
    setSelected(null);
    load();
  };

  return (
    <div className="flex flex-1 min-h-0">
      {/* list */}
      <div className="w-64 shrink-0 border-r border-border flex flex-col">
        <div className="h-12 border-b border-border flex items-center px-3 gap-2">
          <Truck className="h-4 w-4 text-primary" />
          <span className="font-head font-bold text-sm">Equipos</span>
          <div className="flex-1" />
          {canWrite && (
            <Button size="icon" className="h-7 w-7 rounded-sm" onClick={() => setDialog({ mode: "create", data: { codigo: "", descripcion: "", params: { ...emptyParams } } })} data-testid="new-equipo-btn">
              <Plus className="h-4 w-4" />
            </Button>
          )}
        </div>
        <div className="overflow-y-auto flex-1">
          {items.map((eq) => (
            <button
              key={eq.id}
              onClick={() => setSelected(eq)}
              data-testid={`equipo-item-${eq.codigo}`}
              className={`w-full text-left px-3 py-2 border-b border-border/60 transition-colors ${selected?.id === eq.id ? "bg-accent/60 border-l-2 border-l-primary" : "hover:bg-accent/40 border-l-2 border-l-transparent"}`}
            >
              <div className="font-mono-num text-xs text-primary">{eq.codigo}</div>
              <div className="text-sm truncate">{eq.descripcion}</div>
              <div className="font-mono-num text-xs text-muted-foreground mt-0.5">$ {fmt(eq.costo?.costo_horario, 2)} /h</div>
            </button>
          ))}
        </div>
      </div>

      {/* detail */}
      <div className="flex-1 overflow-auto">
        {selected ? (
          <div className="p-4 max-w-3xl">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="font-head font-bold text-lg">{selected.descripcion}</h2>
                <span className="font-mono-num text-xs text-primary">{selected.codigo}</span>
              </div>
              {canWrite && (
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" className="h-8 rounded-sm gap-1" onClick={() => setDialog({ mode: "edit", data: { ...selected, params: { ...selected.params } } })} data-testid="edit-equipo-btn">
                    <Pencil className="h-3.5 w-3.5" /> Editar
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 rounded-sm text-destructive gap-1" onClick={() => remove(selected)} data-testid="delete-equipo-btn">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-border rounded-sm">
                <div className="px-3 h-9 flex items-center border-b border-border bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">Parámetros</div>
                <table className="qc-table">
                  <tbody>
                    {PARAM_FIELDS.map((f) => (
                      <tr key={f.key}>
                        <td>{f.label}</td>
                        <td className="text-right font-mono-num">{fmt(selected.params[f.key], f.dec)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border border-primary/40 rounded-sm h-fit">
                <div className="px-3 h-9 flex items-center border-b border-border bg-primary/10 text-xs uppercase tracking-wide text-primary font-medium">Costo horario</div>
                <table className="qc-table">
                  <tbody>
                    {COST_ROWS.map(([k, label]) => (
                      <tr key={k}>
                        <td>{label}</td>
                        <td className="text-right font-mono-num">{fmt(selected.costo?.[k], 6)}</td>
                      </tr>
                    ))}
                    <tr className="bg-primary/10">
                      <td className="font-medium">Costo horario total</td>
                      <td className="text-right font-mono-num font-medium text-primary">$ {fmt(selected.costo?.costo_horario, 6)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-10 text-muted-foreground text-sm">Selecciona o crea un equipo.</div>
        )}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-head">{dialog?.mode === "edit" ? "Editar equipo" : "Nuevo equipo"}</DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Código</Label>
                  <Input className="mt-1 font-mono-num" value={dialog.data.codigo} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, codigo: e.target.value } }))} data-testid="equipo-codigo" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Descripción</Label>
                  <Input className="mt-1" value={dialog.data.descripcion} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, descripcion: e.target.value } }))} data-testid="equipo-descripcion" />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-auto pr-1">
                {PARAM_FIELDS.map((f) => (
                  <div key={f.key}>
                    <Label className="text-[10px] leading-tight block">{f.label}</Label>
                    <Input
                      type="number"
                      step="any"
                      className="mt-0.5 h-8 font-mono-num text-right"
                      value={dialog.data.params[f.key]}
                      onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, params: { ...d.data.params, [f.key]: parseFloat(e.target.value) || 0 } } }))}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDialog(null)}>Cancelar</Button>
            <Button className="rounded-sm" onClick={save} data-testid="equipo-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
